import { MEETING_AUDIO_OUTPUT_KEY } from './meetingAudio';

let audioContext = null;
let unlockInstalled = false;
let unlocked = false;
let outputChangeInstalled = false;
let requestedOutputDeviceId = null;
let outputRouteContext = null;
let outputRouteDeviceId = null;
let outputRouteQueue = Promise.resolve(false);
const lastPlayedByKey = new Map();
const playbackInFlight = new Set();
let tonePlaybackBusyUntil = 0;

const clean = (value) => String(value || '').trim();

const getStoredOutputDeviceId = () => {
  if (typeof window === 'undefined') return '';
  try {
    return clean(localStorage.getItem(MEETING_AUDIO_OUTPUT_KEY));
  } catch {
    return '';
  }
};

const getRequestedOutputDeviceId = () =>
  requestedOutputDeviceId === null
    ? getStoredOutputDeviceId()
    : requestedOutputDeviceId;

/**
 * Web Audio notification tones have their own AudioContext, so routing remote
 * <audio> elements is not enough. Keep this context on the same persisted
 * Bluetooth/headset output and fall back to the system speaker without
 * interrupting the meeting when a device disappears.
 */
export const routeMeetingToneOutput = (
  context,
  deviceId = getRequestedOutputDeviceId()
) => {
  const requested = clean(deviceId);
  if (!context || context.state === 'closed') return Promise.resolve(false);
  if (typeof context.setSinkId !== 'function') {
    return Promise.resolve(requested === '');
  }

  const applyRoute = async () => {
    if (context.state === 'closed') return false;
    if (
      outputRouteContext === context &&
      outputRouteDeviceId === requested
    ) {
      return true;
    }

    try {
      await context.setSinkId(requested);
      outputRouteContext = context;
      outputRouteDeviceId = requested;
      return true;
    } catch (error) {
      if (requested) {
        try {
          await context.setSinkId('');
          outputRouteContext = context;
          outputRouteDeviceId = '';
        } catch {
          // The operating system still owns the final output fallback.
        }
      }
      console.warn(
        'Meeting notification output fallback:',
        error?.message || error
      );
      return false;
    }
  };

  const queuedRoute = outputRouteQueue
    .catch(() => false)
    .then(applyRoute);
  outputRouteQueue = queuedRoute;
  return queuedRoute;
};

const handleMeetingOutputDeviceChange = (event) => {
  const eventDeviceId = event?.detail?.deviceId;
  requestedOutputDeviceId =
    typeof eventDeviceId === 'string'
      ? clean(eventDeviceId)
      : getStoredOutputDeviceId();

  if (audioContext && audioContext.state !== 'closed') {
    void routeMeetingToneOutput(audioContext, requestedOutputDeviceId);
  }
};

const ensureOutputChangeListener = () => {
  if (typeof window === 'undefined' || outputChangeInstalled) return;
  requestedOutputDeviceId = getStoredOutputDeviceId();
  window.addEventListener(
    'meeting-output-device-change',
    handleMeetingOutputDeviceChange
  );
  outputChangeInstalled = true;
};

const removeOutputChangeListener = () => {
  if (typeof window === 'undefined' || !outputChangeInstalled) return;
  window.removeEventListener(
    'meeting-output-device-change',
    handleMeetingOutputDeviceChange
  );
  outputChangeInstalled = false;
};

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  ensureOutputChangeListener();

  const AudioContextClass =
    window.AudioContext ||
    window.webkitAudioContext;

  if (!AudioContextClass) return null;

  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new AudioContextClass();
    outputRouteContext = null;
    outputRouteDeviceId = null;
  }

  return audioContext;
};

const prepareMeetingAudioContext = async () => {
  const context = getAudioContext();
  if (!context) return null;

  if (context.state === 'suspended') {
    await context.resume();
  }

  await routeMeetingToneOutput(context);
  return context.state === 'running' ? context : null;
};

export const unlockMeetingAudio = async () => {
  try {
    const context = await prepareMeetingAudioContext();
    if (!context) return false;

    // A silent one-sample buffer permanently unlocks audio after user gesture.
    const buffer = context.createBuffer(1, 1, context.sampleRate);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start(0);

    unlocked = context.state === 'running';
    return unlocked;
  } catch (error) {
    console.warn('Meeting audio unlock failed:', error);
    return false;
  }
};

export const installMeetingAudioUnlock = () => {
  if (typeof window === 'undefined' || unlockInstalled) {
    return () => undefined;
  }

  unlockInstalled = true;
  ensureOutputChangeListener();

  const unlock = () => {
    void unlockMeetingAudio();
  };

  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('touchstart', unlock, { passive: true });
  window.addEventListener('keydown', unlock);

  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('touchstart', unlock);
    window.removeEventListener('keydown', unlock);
    removeOutputChangeListener();
    unlockInstalled = false;
  };
};

const playTone = (context, startAt, frequency, duration, gainValue) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, startAt);

  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(gainValue, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);

  oscillator.start(startAt);
  oscillator.stop(startAt + duration + 0.03);
};

const reserveTonePlayback = (key, dedupeWindowMs, audibleDurationMs) => {
  const now = Date.now();
  const previousAt = Number(lastPlayedByKey.get(key) || 0);
  if (
    playbackInFlight.size > 0 ||
    now - previousAt < dedupeWindowMs ||
    now < tonePlaybackBusyUntil
  ) {
    return false;
  }

  lastPlayedByKey.set(key, now);
  playbackInFlight.add(key);
  tonePlaybackBusyUntil = now + audibleDurationMs;
  return true;
};

const markToneAudibleFor = (durationMs) => {
  tonePlaybackBusyUntil = Math.max(
    tonePlaybackBusyUntil,
    Date.now() + durationMs
  );
};

export const playJoinRequestSound = async (dedupeKey = '') => {
  const key = String(dedupeKey || 'join-request');

  // Socket event + room update fallback may arrive together.
  if (!reserveTonePlayback(key, 2500, 550)) return false;

  try {
    const context = await prepareMeetingAudioContext();
    if (!context) return false;

    unlocked = true;

    // Local-only, short, low-gain tones stay outside the microphone capture
    // graph and give browser echo cancellation the smallest possible signal.
    markToneAudibleFor(550);
    const startAt = context.currentTime + 0.01;
    playTone(context, startAt, 784, 0.16, 0.08);
    playTone(context, startAt + 0.18, 1046.5, 0.24, 0.09);

    return true;
  } catch (error) {
    console.warn('Join request sound failed:', error);
    return false;
  } finally {
    playbackInFlight.delete(key);
  }
};


export const playClassStartSound = async (dedupeKey = '') => {
  const key = String(dedupeKey || 'class-start');
  if (!reserveTonePlayback(key, 5000, 750)) return false;

  try {
    const context = await prepareMeetingAudioContext();
    if (!context) return false;

    unlocked = true;
    markToneAudibleFor(750);
    const startAt = context.currentTime + 0.01;
    playTone(context, startAt, 659.25, 0.14, 0.075);
    playTone(context, startAt + 0.16, 783.99, 0.14, 0.08);
    playTone(context, startAt + 0.32, 1046.5, 0.28, 0.09);
    return true;
  } catch (error) {
    console.warn('Class start sound failed:', error);
    return false;
  } finally {
    playbackInFlight.delete(key);
  }
};

export const isMeetingAudioUnlocked = () => unlocked;
